import '../../scss/ExamDetail.scss'
import { Button } from 'primereact/button';
import { FaLongArrowAltLeft, FaEye } from "react-icons/fa";
import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import ExamResultChart from './ExamResultChart';
import { ThiTrucTuyenService } from 'services/ThiTrucTuyenService';

export default function ExamHistory() {
    const userId = localStorage.getItem('User_Id');
    const navigate = useNavigate();
    const [examHistory, setExamHistory] = useState([]);
    const [selectedExam, setSelectedExam] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadExamHistory();
    }, []);

    const loadExamHistory = async () => {
        setLoading(true);
        try {
            // Lấy dữ liệu từ API thay vì localStorage
            const result = await ThiTrucTuyenService.getExamHistory(userId);
            
            if (result?.data?.status === 'success' && result.data.scores && result.data.scores.length > 0) {
                // Nhóm scores theo attempt_code
                const groupedByAttempt = {};
                
                result.data.scores.forEach((score) => {
                    const attemptCode = score.attempt_code || `NO_ATTEMPT_${score.dethitructuyen_id || score.dethi_id}_${score.time_bat_dau_thi}`;
                    
                    if (!groupedByAttempt[attemptCode]) {
                        groupedByAttempt[attemptCode] = {
                            attempt_code: attemptCode,
                            scores: [],
                            examId: score.dethitructuyen_id || score.dethi_id,
                            examName: score.dethitructuyen_name || 'Đề thi',
                            // Lấy thời gian kết thúc từ score cuối cùng (hoặc thời gian bắt đầu nếu chưa kết thúc)
                            date: null,
                            time_bat_dau_thi: score.time_bat_dau_thi
                        };
                    }
                    
                    groupedByAttempt[attemptCode].scores.push(score);
                    
                    // Cập nhật date = thời gian kết thúc của dạng bài cuối cùng
                    if (score.time_ket_thuc_thi) {
                        const currentDate = new Date(score.time_ket_thuc_thi);
                        const existingDate = groupedByAttempt[attemptCode].date ? new Date(groupedByAttempt[attemptCode].date) : null;
                        
                        if (!existingDate || currentDate > existingDate) {
                            groupedByAttempt[attemptCode].date = score.time_ket_thuc_thi;
                        }
                    } else if (!groupedByAttempt[attemptCode].date) {
                        // Nếu chưa có time_ket_thuc_thi, dùng time_bat_dau_thi
                        groupedByAttempt[attemptCode].date = score.time_bat_dau_thi;
                    }
                });
                
                // Chuyển đổi sang format giống như localStorage
                const history = Object.values(groupedByAttempt).map((exam) => {
                    // Tính tổng số câu và số câu đúng
                    let totalQuestions = 0;
                    let totalCorrect = 0;
                    
                    exam.scores.forEach((score) => {
                        const tongsocau = parseInt(score.tongsocau || 0);
                        const socaudung = parseInt(score.socaudung || 0);
                        totalQuestions += tongsocau;
                        totalCorrect += socaudung;
                    });
                    
                    return {
                        attempt_code: exam.attempt_code,
                        examId: exam.examId,
                        examName: exam.examName,
                        date: exam.date,
                        scores: exam.scores,
                        totalQuestions: totalQuestions,
                        totalCorrect: totalCorrect
                    };
                });
                
                // Sắp xếp theo ngày thi (mới nhất trước)
                history.sort((a, b) => {
                    const dateA = new Date(a.date);
                    const dateB = new Date(b.date);
                    return dateB - dateA;
                });
                
                setExamHistory(history);
            } else {
                // Nếu không có dữ liệu từ API, thử fallback về localStorage
                const historyKey = `exam_history_${userId}`;
                const localHistory = JSON.parse(localStorage.getItem(historyKey) || '[]');
                setExamHistory(localHistory);
            }
        } catch (error) {
            console.error('Error loading exam history from API:', error);
            // Fallback về localStorage nếu API lỗi
            try {
                const historyKey = `exam_history_${userId}`;
                const localHistory = JSON.parse(localStorage.getItem(historyKey) || '[]');
                setExamHistory(localHistory);
            } catch (e) {
                console.error('Error loading exam history from localStorage:', e);
                setExamHistory([]);
            }
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleString('vi-VN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return dateString;
        }
    };

    const calculateOverallScore = (scores) => {
        if (!scores || scores.length === 0) return 0;
        const skillScores = [];
        ['NGUPHAP', 'NGHE', 'DOC', 'VIET', 'NOI'].forEach(skill => {
            const scoreItem = scores.find(s => s.type_exam === skill);
            if (scoreItem && scoreItem.tongsocau && scoreItem.tongsocau > 0) {
                const score = Math.round((parseFloat(scoreItem.socaudung || 0) / parseFloat(scoreItem.tongsocau)) * 100);
                skillScores.push(score);
            }
        });
        if (skillScores.length === 0) return 0;
        return Math.round(skillScores.reduce((sum, val) => sum + val, 0) / skillScores.length);
    };

    const handleViewResult = (exam) => {
        setSelectedExam(exam);
    };

    const handleBackToList = () => {
        setSelectedExam(null);
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <ProgressSpinner />
            </div>
        );
    }

    if (selectedExam) {
        return (
            <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
                <Button
                    className="mb-3"
                    icon={<FaLongArrowAltLeft />}
                    label="Quay lại danh sách"
                    onClick={handleBackToList}
                    style={{ backgroundColor: "#23085a", color: 'white', border: 'none' }}
                />
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#23085a', marginBottom: 8 }}>
                        Kết quả thi: {selectedExam.examName}
                    </h2>
                    <p style={{ fontSize: '16px', color: '#666' }}>
                        Ngày thi: {formatDate(selectedExam.date)}
                    </p>
                </div>
                <ExamResultChart scores={selectedExam.scores} />
                <div className="conatiner-div1" style={{ marginTop: 24 }}>
                    <h3 style={{ fontWeight: 600, fontSize: 18, marginBottom: 12 }}>Chi tiết kết quả</h3>
                    <div className="table-responsive">
                        <table className="table table-striped">
                            <thead>
                                <tr>
                                    <th>Dạng bài</th>
                                    <th>Đúng / Tổng</th>
                                    <th>Tỷ lệ</th>
                                    <th>Tổng điểm</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedExam.scores.map((row, idx) => {
                                    const percentage = row.tongsocau > 0 
                                        ? Math.round((parseFloat(row.socaudung || 0) / parseFloat(row.tongsocau)) * 100)
                                        : 0;
                                    return (
                                        <tr key={idx}>
                                            <td>{row.type_exam}</td>
                                            <td>
                                                <span style={{ color: percentage === 100 ? 'green' : percentage >= 60 ? 'orange' : 'red' }}>
                                                    {row.socaudung || 0}
                                                </span>
                                                /{row.tongsocau || 0}
                                            </td>
                                            <td>{percentage}%</td>
                                            <td>{row.tong_diem || '0'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#23085a', margin: 0 }}>
                    Lịch sử thi
                </h2>
                <Button
                    icon={<FaLongArrowAltLeft />}
                    label="Quay lại"
                    onClick={() => navigate('/overview')}
                    style={{ backgroundColor: "#23085a", color: 'white', border: 'none' }}
                />
            </div>

            {examHistory.length === 0 ? (
                <div className="conatiner-div1" style={{ textAlign: 'center', padding: 40 }}>
                    <p style={{ fontSize: 18, color: '#666' }}>Bạn chưa có lịch sử thi nào.</p>
                    <p style={{ fontSize: 14, color: '#999', marginTop: 8 }}>
                        Hoàn thành một bài thi để xem kết quả tại đây.
                    </p>
                </div>
            ) : (
                <div style={{ display: 'grid', gap: 16 }}>
                    {examHistory.map((exam, index) => {
                        const overallScore = calculateOverallScore(exam.scores);
                        return (
                            <div
                                key={index}
                                className="conatiner-div1"
                                style={{
                                    padding: 20,
                                    borderRadius: 8,
                                    border: '1px solid #e0e0e0',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    backgroundColor: '#fff'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = 'none';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                                onClick={() => handleViewResult(exam)}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1 }}>
                                        <h3 style={{ fontSize: 20, fontWeight: 600, color: '#23085a', marginBottom: 8 }}>
                                            {exam.examName}
                                        </h3>
                                        <p style={{ fontSize: 14, color: '#666', marginBottom: 12 }}>
                                            Ngày thi: {formatDate(exam.date)}
                                        </p>
                                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                            <div>
                                                <span style={{ fontSize: 12, color: '#999' }}>Tổng câu đúng: </span>
                                                <span style={{ fontSize: 16, fontWeight: 600, color: '#23085a' }}>
                                                    {exam.totalCorrect} / {exam.totalQuestions}
                                                </span>
                                            </div>
                                            <div>
                                                <span style={{ fontSize: 12, color: '#999' }}>Overall Score: </span>
                                                <span style={{
                                                    fontSize: 16,
                                                    fontWeight: 600,
                                                    color: overallScore >= 80 ? '#11c611' : overallScore >= 60 ? '#ffc107' : '#dc3545'
                                                }}>
                                                    {overallScore}%
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        <Button
                                            icon={<FaEye />}
                                            label="Kết quả"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleViewResult(exam);
                                            }}
                                            style={{
                                                backgroundColor: "#23085a",
                                                color: 'white',
                                                border: 'none',
                                                width: '100%'
                                            }}
                                        />
                                        <Button
                                            icon={<FaEye />}
                                            label="Xem bài làm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/review-exam/${exam.attempt_code}`);
                                            }}
                                            style={{
                                                backgroundColor: "#ffffff",
                                                color: '#23085a',
                                                border: '1px solid #23085a',
                                                width: '100%'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

